import { getApps, initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import {
  frontendConfigError,
  getRuntimeConfig,
  onRuntimeConfigChange,
  requireRuntimeConfig,
  validateFrontendRuntimeConfig,
} from './runtimeConfig';

let initializedApp = null;
let initializedAuth = null;
let initializedDb = null;
let initializedConfigKey = null;

function applyLocalRecaptchaSetting(authInstance) {
  // Disable reCAPTCHA for local development only
  // Fail-closed: requires both development mode AND explicit opt-in flag
  if (
    authInstance &&
    typeof process !== 'undefined' &&
    process.env?.NODE_ENV === 'development' &&
    process.env?.REACT_APP_DISABLE_RECAPTCHA === 'true'
  ) {
    authInstance.settings = authInstance.settings || {};
    authInstance.settings.appVerificationDisabledForTesting = true;
    console.log("🔓 reCAPTCHA verification disabled for development");
  }
}

function createUninitializedServiceProxy(serviceName, getInitializedInstance) {
  return new Proxy(
    {},
    {
      get(_target, prop) {
        const instance = getInitializedInstance();
        if (instance) {
          const value = Reflect.get(instance, prop, instance);
          return typeof value === 'function' ? value.bind(instance) : value;
        }
        if (prop === '__esModule' || prop === '$$typeof' || prop === Symbol.toStringTag) {
          return undefined;
        }
        throw frontendConfigError(
          `Firebase ${serviceName} is unavailable because frontend runtime configuration has not been loaded or is invalid.`
        );
      },
      set(_target, prop, value) {
        const instance = getInitializedInstance();
        if (instance) {
          instance[prop] = value;
          return true;
        }
        throw frontendConfigError(
          `Firebase ${serviceName} is unavailable because frontend runtime configuration has not been loaded or is invalid.`
        );
      },
    }
  );
}

export let app = createUninitializedServiceProxy('app', () => initializedApp);
export let auth = createUninitializedServiceProxy('auth', () => initializedAuth);
export let db = createUninitializedServiceProxy('db', () => initializedDb);

export function initializeFirebaseClient(runtimeConfigInput = getRuntimeConfig()) {
  const validated = runtimeConfigInput
    ? validateFrontendRuntimeConfig(runtimeConfigInput)
    : requireRuntimeConfig();

  const firebaseConfig = validated.firebase;
  const nextConfigKey = JSON.stringify(firebaseConfig);

  if (initializedApp && initializedAuth && initializedDb && initializedConfigKey === nextConfigKey) {
    return { app: initializedApp, auth: initializedAuth, db: initializedDb, firebaseConfig };
  }

  const rawExistingApps = typeof getApps === 'function' ? getApps() : [];
  const existingApps = Array.isArray(rawExistingApps) ? rawExistingApps : [];
  const matchingExistingApp = existingApps.find(
    (existing) =>
      existing?.options?.projectId === firebaseConfig.projectId &&
      existing?.options?.appId === firebaseConfig.appId
  );
  initializedApp = matchingExistingApp || initializeApp(firebaseConfig);
  initializedAuth = getAuth(initializedApp);
  initializedDb = getFirestore(initializedApp);
  initializedConfigKey = nextConfigKey;

  applyLocalRecaptchaSetting(initializedAuth);

  app = initializedApp;
  auth = initializedAuth;
  db = initializedDb;

  return { app: initializedApp, auth: initializedAuth, db: initializedDb, firebaseConfig };
}

export function getFirebaseClientConfig(runtimeConfigInput = getRuntimeConfig()) {
  const validated = runtimeConfigInput
    ? validateFrontendRuntimeConfig(runtimeConfigInput)
    : requireRuntimeConfig();
  return validated.firebase;
}

export function resetFirebaseClientForTesting() {
  initializedApp = null;
  initializedAuth = null;
  initializedDb = null;
  initializedConfigKey = null;
  app = createUninitializedServiceProxy('app', () => initializedApp);
  auth = createUninitializedServiceProxy('auth', () => initializedAuth);
  db = createUninitializedServiceProxy('db', () => initializedDb);
}

onRuntimeConfigChange((nextConfig) => {
  if (nextConfig) {
    initializeFirebaseClient(nextConfig);
  }
});

const initialRuntimeConfig = getRuntimeConfig({ throwOnInvalid: true });
if (initialRuntimeConfig) {
  initializeFirebaseClient(initialRuntimeConfig);
}